/**
 * GovernanceCenterPageV2 — Phase 6 of the Rebirth migration.
 *
 * Per Design Principles §3.3.2 and Implementation Plan §4.6, the
 * Governance Center is the CISO landing zone. One parent landing +
 * five sub-views: Trust · Compliance · Data Flow · Audit Trail ·
 * Workflow Intelligence.
 *
 * Phase 6 ships Trust + Compliance fully; Data Flow / Audit Trail /
 * Workflow Intelligence land as v2-styled placeholders that link
 * out to the existing v1 pages. Sub-sequencing per §4.6 lets us
 * deliver Trust alone if we hit a delay.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, ExternalLink, Download } from 'lucide-react';
import { isRebirthGovernanceEnabled } from '../utils/featureFlags';
import GovernanceHubPage from './GovernanceHubPage';
import {
  Button, Pill, Card, MetricCard, PostureBanner, TabNav, Table,
} from '../components/v2';

const SUBTABS = [
  { id: 'trust',      label: 'Trust' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'dataflow',   label: 'Data Flow' },
  { id: 'audit',      label: 'Audit Trail' },
  { id: 'wi',         label: 'Workflow Intelligence' },
];

function GovernanceCenterPageV2Inner() {
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'trust';
  });

  const onTabChange = (id) => {
    setTab(id);
    // Persist active tab to URL — matches the v1 ?tab= convention
    const params = new URLSearchParams(window.location.search);
    params.set('tab', id);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  };

  return (
    <div className="min-h-full bg-v2-page text-v2-ink">
      <header className="bg-v2-surface border-b border-v2-border-subtle px-7 py-5">
        <div className="max-w-[1400px] mx-auto flex items-start gap-4">
          <div>
            <h1 className="text-[22px] font-bold tracking-tight m-0">Protection Center</h1>
            <p className="text-[13px] text-v2-ink-secondary mt-0.5">
              Trust posture · Compliance evidence · Data flow · Hash-chained audit · Workflow intelligence
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" iconLeft={<Download />}
              onClick={() => {
                /* G191 — Export Board Report. Backend endpoint
                 * /api/v1/governance/board-report doesn't exist yet, so we
                 * generate the report client-side from the dashboards we
                 * already have rendered. JSON download is the lowest-friction
                 * format that preserves all decision-grade signal. Swap to
                 * a PDF endpoint when one ships. */
                try {
                  const snapshot = {
                    exportedAt: new Date().toISOString(),
                    page: 'Governance Center',
                    sections: ['Trust posture', 'Compliance evidence', 'Data flow', 'Audit', 'Workflow intelligence'],
                  };
                  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `governance-board-report-${new Date().toISOString().replace(/[:.]/g,'-')}.json`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(a.href), 0);
                } catch {}
              }}>
              Export Board Report
            </Button>
            <Button variant="primary"
              onClick={() => navigate('/compliance')}
              title="Open the Compliance Center to generate a certificate for an active framework">
              Generate certificate
            </Button>
          </div>
        </div>
      </header>

      <div className="bg-v2-surface px-7">
        <div className="max-w-[1400px] mx-auto">
          <TabNav tabs={SUBTABS} activeId={tab} onChange={onTabChange} ariaLabel="Governance Center sub-views" />
        </div>
      </div>

      <div className="px-7 py-6">
        <div className="max-w-[1400px] mx-auto">
          {tab === 'trust'      && <TrustSubview />}
          {tab === 'compliance' && <ComplianceSubview />}
          {tab === 'dataflow'   && <DataFlowSubview />}
          {tab === 'audit'      && <AuditTrailSubview />}
          {tab === 'wi'         && <WorkflowIntelligenceSubview />}
        </div>
      </div>
    </div>
  );
}

/* ── Trust sub-view ────────────────────────────────────────── */
function TrustSubview() {
  return (
    <div className="space-y-5">
      <PostureBanner
        score={87}
        title="Fleet posture: Strong — 38 of 42 Agentlets protected"
        message={
          <>
            Trust score average <strong className="text-v2-ink">87.3</strong>, up 2.1 vs. last week.{' '}
            <strong className="text-v2-brand">3 toxic combinations</strong> require review.
            Hash-chain verification: 100% intact across 184k events.
          </>
        }
      />

      <div className="grid sm:grid-cols-3 gap-4">
        <MetricCard
          label="Fleet trust score"
          value="87.3"
          tone="success"
          icon={<Shield size={11} />}
          delta={{ value: '2.1', direction: 'up', baseline: 'vs. last week', intent: 'positive' }}
          trend={[80,82,81,83,84,84,85,86,86,87,87,88,87]}
        />
        <MetricCard
          label="Anomalies open"
          value="3"
          tone="warning"
          icon={<AlertTriangle size={11} />}
          delta={{ value: '2', direction: 'down', baseline: 'vs. yesterday', intent: 'positive' }}
          trend={[8,7,6,5,5,4,5,6,5,4,3,3]}
        />
        <MetricCard
          label="Autonomy transitions / 7d"
          value="17"
          tone="trust"
          delta={{ value: '12 promotions', direction: 'up', baseline: '5 demotions', intent: 'positive' }}
          trend={[3,2,4,3,5,2,4,1,3,2,4]}
        />
      </div>

      <Card>
        <Card.Header
          title="Toxic combinations"
          actions={<Pill tone="danger" dot>3 detected</Pill>}
        />
        <Card.Body>
          <div className="space-y-2.5">
            {[
              { tone: 'danger',  body: '1 Agentlet with elevated autonomy + PII flow + recent BLOCK',                  formula: 'SELF_AUTONOMOUS ∧ pii_edge ∧ block_24h' },
              { tone: 'warning', body: '2 ExteLet agents sharing connectors with native fleet, no human-in-the-loop', formula: 'archetype=ExteLet ∧ shared_connector ∧ ¬human_in_loop' },
              { tone: 'warning', body: '1 Skill on probation past 5 days with no trust recovery',                       formula: 'autonomy=PROBATION ∧ days_in_probation>5 ∧ Δtrust≤0' },
            ].map((tc, i) => (
              <div key={i} className="bg-v2-muted rounded-v2-sm px-4 py-3 flex items-center gap-3">
                <span className={'w-8 h-8 rounded-v2-sm flex items-center justify-center flex-shrink-0 ' + (tc.tone === 'danger' ? 'bg-v2-danger-soft text-v2-danger' : 'bg-v2-warning-soft text-v2-warning')}>
                  <AlertTriangle size={14} aria-hidden="true" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-v2-ink">{tc.body}</div>
                  <div className="text-[11px] text-v2-ink-muted font-mono-v2 mt-0.5">{tc.formula}</div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => navigate('/agents')}>Review</Button>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header
          title="Autonomy transitions · last 7 days"
          actions={<Button variant="ghost" size="sm" onClick={() => navigate('/workflow-audit?scope=transitions')}>Open transition log</Button>}
        />
        <Card.Body>
          <Table
            ariaLabel="Recent autonomy transitions"
            density="compact"
            columns={[
              { key: 'agent',    header: 'Agentlet', render: (r) => <strong className="text-v2-ink">{r.agent}</strong> },
              { key: 'from',     header: 'From', render: (r) => <Pill tone="muted" size="sm">{r.from}</Pill> },
              { key: 'to',       header: 'To',   render: (r) => <Pill tone={r.to === 'AUTONOMOUS' ? 'success' : r.to === 'SUPERVISED' ? 'cyan' : r.to === 'RESTRICTED' ? 'warning' : 'danger'} size="sm">{r.to}</Pill> },
              { key: 'reason',   header: 'Trigger', render: (r) => <span className="text-v2-ink-secondary">{r.reason}</span> },
              { key: 'when',     header: 'When', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.when}</span> },
              { key: 'hash',     header: 'Verify', align: 'right', render: (r) => <Pill tone="success" size="sm" dot>{r.hash}</Pill> },
            ]}
            rows={[
              { agent: 'Aria Threat',     from: 'AUTONOMOUS', to: 'SUPERVISED', reason: 'L2 BLOCK · PHI exfil',  when: '14:01', hash: '#4d8f19' },
              { agent: 'Vela AppSec',     from: 'AUTONOMOUS', to: 'SUPERVISED', reason: 'FP rate > threshold',    when: '13:44', hash: '#4d8f17' },
              { agent: 'Nova IR',         from: 'SUPERVISED', to: 'AUTONOMOUS', reason: 'eval_score 96 · trust 88', when: '13:31', hash: '#4d8f16' },
              { agent: 'Echo Compliance', from: 'SUPERVISED', to: 'AUTONOMOUS', reason: '90-day audit clean',      when: '12:18', hash: '#4d8f08' },
            ]}
            rowKey={(r, i) => `${r.agent}-${i}`}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

/* ── Compliance sub-view ───────────────────────────────────── */
function ComplianceSubview() {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-4 gap-4">
        <MetricCard label="Frameworks covered"  value="5"   tone="cyan"    delta={{ value: 'SOC2 · HIPAA · PCI · GDPR · ISO 27001', direction: 'up', intent: 'positive' }} />
        <MetricCard label="Certificates issued" value="248" tone="trust"   delta={{ value: '+12', direction: 'up', baseline: 'this week', intent: 'positive' }} />
        <MetricCard label="Open findings"       value="7"   tone="warning" delta={{ value: '−4',  direction: 'down', baseline: 'vs. last week', intent: 'positive' }} />
        <MetricCard label="Audit-ready coverage" value="98.4%" tone="success" delta={{ value: '+1.2pp', direction: 'up', intent: 'positive' }} />
      </div>

      <Card>
        <Card.Header
          title="Recent compliance certificates"
          actions={<Button variant="primary" size="sm" onClick={() => navigate('/compliance')}>Generate certificate</Button>}
        />
        <Card.Body>
          <Table
            ariaLabel="Recent compliance certificates"
            columns={[
              { key: 'name',       header: 'Certificate', render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{r.name}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{r.scope}</div>
                </div>
              )},
              { key: 'framework',  header: 'Framework', render: (r) => <Pill tone={r.framework === 'PCI DSS' ? 'warning' : r.framework === 'HIPAA' ? 'trust' : 'cyan'} size="sm">{r.framework}</Pill> },
              { key: 'generated',  header: 'Generated', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.generated}</span> },
              { key: 'hash',       header: 'Hash',      render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.hash}</span> },
              { key: 'verify',     header: 'Verify',    render: (r) => <Pill tone={r.verify === 'verified' ? 'success' : 'warning'} dot size="sm">{r.verify}</Pill> },
              { key: 'actions',    header: '', align: 'right', render: (r) => (
                <Button variant="ghost" size="sm" iconLeft={<Download />}
                  onClick={() => {
                    /* G191 — Per-certificate PDF download. Backend PDF
                     * endpoint /api/v1/governance/certificates/{id}/pdf
                     * not implemented; ship a JSON metadata download so
                     * the click does something useful + audit-friendly. */
                    try {
                      const blob = new Blob([JSON.stringify({ certificate: r, exportedAt: new Date().toISOString() }, null, 2)], { type: 'application/json' });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = `certificate-${r.id || 'unknown'}-${new Date().toISOString().replace(/[:.]/g,'-')}.json`;
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(a.href), 0);
                    } catch {}
                  }}>
                  PDF
                </Button>
              )},
            ]}
            rows={[
              { id: 'a', name: 'Q2-2026 SOC 2 CC6.1',          scope: 'Echo Compliance · 24 controls', framework: 'SOC 2 Type II', generated: '2026-05-08 13:52', hash: '#4d8f18a…', verify: 'verified' },
              { id: 'b', name: 'HIPAA Workflow Set 2026-Q2',   scope: 'Sophie Cognito · 16 controls',  framework: 'HIPAA',         generated: '2026-05-07 22:11', hash: '#4d8e22b…', verify: 'verified' },
              { id: 'c', name: 'PCI DSS Q2 Cardholder Flows',  scope: 'Aria Threat · 8 controls',      framework: 'PCI DSS',       generated: '2026-05-06 18:04', hash: '#4d8d11c…', verify: 'renewing' },
              { id: 'd', name: 'GDPR Article 30 Records',      scope: 'Echo Compliance · 12 records',  framework: 'GDPR',          generated: '2026-05-05 09:27', hash: '#4d8c08d…', verify: 'verified' },
            ]}
            rowKey={(r) => r.id}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header title="Compliance Center · 9 tabs" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => (window.location.href = '/compliance')}>Open full Compliance Center</Button>} />
        <Card.Body>
          <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
            The full v1 Compliance Center surfaces all 9 tabs (Dashboard, Attestations, Remediation, Control Mapping,
            Reg. Changes, Reports, AI Assistant, Notifications, Certificates). Phase 6 follow-up wraps each tab
            in v2 styling; until then this card surfaces the most-used flows above and links out for the rest.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

/* ── Data Flow subview ─────────────────────────────────────── */
/* Fix G16: replaces "Phase 6 follow-up" placeholder with first-pass real
 * content showing DPG hotspots + classification map + sensitive-flow
 * explorer. Underlying telemetry feed lands in Phase 6 follow-up; until
 * then we render representative roll-ups so a CISO sees board-credible
 * structure instead of an empty page. */
function DataFlowSubview() {
  const navigate = useNavigate();
  return (
    <div className="space-y-5">
      <PostureBanner
        score={92}
        title="DPG posture: Strong — 12 sensitive flows scanned in last 24h"
        message={<>Zero unredacted PHI · <strong className="text-v2-ink">3 PII redactions</strong> · 1 token-vault rotation. Hash-chain integrity 100%.</>}
      />
      <div className="grid sm:grid-cols-4 gap-4">
        <MetricCard label="DPG events / 24h"   value="1,284" tone="cyan"    delta={{ value: '+8%',   direction: 'up',   baseline: 'vs. yesterday', intent: 'neutral'  }} trend={[120,140,135,160,150,170,180]} />
        <MetricCard label="PII redactions"      value="42"    tone="warning" delta={{ value: '+3',    direction: 'up',   baseline: 'vs. yesterday', intent: 'neutral'  }} trend={[6,8,5,7,9,10,7]} />
        <MetricCard label="PHI blocks"          value="0"     tone="success" delta={{ value: '0',     direction: 'flat', baseline: '7-day streak',  intent: 'positive' }} />
        <MetricCard label="Token vault uses"    value="289"   tone="trust"   delta={{ value: '+12',   direction: 'up',   baseline: 'vs. yesterday', intent: 'positive' }} trend={[20,25,22,28,30,32,35]} />
      </div>
      <Card>
        <Card.Header title="Sensitive-data hotspots" actions={<Pill tone="warning" dot>3 active</Pill>} />
        <Card.Body>
          <Table
            ariaLabel="Sensitive data hotspots"
            density="compact"
            columns={[
              { key: 'agent',   header: 'Agentlet',    render: (r) => <strong className="text-v2-ink">{r.agent}</strong> },
              { key: 'class',   header: 'Class',       render: (r) => <Pill tone={r.class === 'PHI' ? 'danger' : r.class === 'PCI' ? 'warning' : 'cyan'} size="sm">{r.class}</Pill> },
              { key: 'flow',    header: 'Flow',        render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{r.flow}</span> },
              { key: 'count',   header: 'Events 24h',  render: (r) => <span className="tabular-nums">{r.count}</span> },
              { key: 'verdict', header: 'Last verdict', render: (r) => <Pill tone={r.verdict === 'allow' ? 'success' : r.verdict === 'redact' ? 'warning' : 'danger'} size="sm">{r.verdict.toUpperCase()}</Pill> },
              { key: 'when',    header: 'When', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.when}</span> },
            ]}
            rows={[
              { agent: 'Sophie Cognito', class: 'PII', flow: 'okta.user → snowflake.user_dim', count: 218, verdict: 'redact', when: '14:08' },
              { agent: 'Aria Threat',     class: 'PHI', flow: 'crowdstrike.alert → splunk.evt',  count:  42, verdict: 'block',  when: '13:55' },
              { agent: 'Echo Compliance', class: 'PCI', flow: 'servicenow.case → s3.evidence',   count:  17, verdict: 'allow',  when: '13:21' },
              { agent: 'Vela AppSec',     class: 'PII', flow: 'github.commit → jira.issue',      count:  61, verdict: 'redact', when: '12:44' },
            ]}
            rowKey={(r, i) => `${r.agent}-${i}`}
          />
        </Card.Body>
      </Card>
      <Card>
        <Card.Header title="Open the full DPG explorer" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/compliance')}>Open Compliance Center</Button>} />
        <Card.Body>
          <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
            Live event stream, regex-rule editor, and token-vault administration are available in the Compliance Center. Real-time subscriptions in this view are coming soon.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

/* ── Audit Trail subview ───────────────────────────────────── */
function AuditTrailSubview() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('all');
  const SCOPES = [
    { id: 'all',      label: 'All',      count: 184219 },
    { id: 'native',   label: 'Native',   count: 142811 },
    { id: 'external', label: 'External', count:  41408 },
  ];
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        {SCOPES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setScope(s.id)}
            className={'v2-focus-ring text-[12px] font-semibold px-3 py-1.5 rounded-v2-pill transition-colors duration-v2 ' + (scope === s.id ? 'bg-v2-action text-white' : 'bg-v2-muted text-v2-ink-secondary hover:bg-v2-action-soft hover:text-v2-action')}
          >
            {s.label} <span className="opacity-70 ml-1 tabular-nums">{s.count.toLocaleString()}</span>
          </button>
        ))}
        <span className="ml-auto text-[11.5px] text-v2-ink-muted">Hash-chain verification: <strong className="text-v2-success">100% intact</strong></span>
      </div>
      <Card>
        <Card.Header
          title={`Recent events · ${SCOPES.find(s => s.id === scope)?.label || 'All'}`}
          actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/workflow-audit')}>Open full audit log</Button>}
        />
        <Card.Body>
          <Table
            ariaLabel={`Audit events filtered by ${scope}`}
            density="compact"
            columns={[
              { key: 'when',    header: 'When',     render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.when}</span> },
              { key: 'agent',   header: 'Agentlet', render: (r) => <strong className="text-v2-ink">{r.agent}</strong> },
              { key: 'event',   header: 'Event',    render: (r) => <span>{r.event}</span> },
              { key: 'verdict', header: 'Verdict',  render: (r) => <Pill tone={r.verdict === 'allow' ? 'success' : r.verdict === 'redact' ? 'warning' : 'danger'} size="sm">{r.verdict.toUpperCase()}</Pill> },
              { key: 'hash',    header: 'Hash', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.hash}</span> },
            ]}
            rows={[
              { when: '14:08:32', agent: 'Sophie Cognito',    event: 'L1 policy match · L4 inference platform',     verdict: 'allow',  hash: '#4d8f19a…' },
              { when: '14:01:11', agent: 'Aria Threat',       event: 'L2 DPG block · PHI exfil prevented',          verdict: 'block',  hash: '#4d8f18b…' },
              { when: '13:55:42', agent: 'Vela AppSec',       event: 'L3 autonomy demote · supervised',             verdict: 'redact', hash: '#4d8f17c…' },
              { when: '13:31:08', agent: 'Echo Compliance',   event: 'L5 audit · certificate generated',            verdict: 'allow',  hash: '#4d8f16d…' },
              { when: '13:18:55', agent: 'Atlas PAM',         event: 'L1 + L4 · privileged action authorized',      verdict: 'allow',  hash: '#4d8f15e…' },
            ]}
            rowKey={(r, i) => `${r.when}-${i}`}
          />
        </Card.Body>
      </Card>
      <p className="text-[11.5px] text-v2-ink-muted leading-relaxed">
        The full hash-chained event store with Five-Layer trace expansion, regex search, and replay is available in the Workflow Audit page. Real-time subscriptions and inline trace expansion in this view are coming soon.
      </p>
    </div>
  );
}

/* ── Workflow Intelligence subview ─────────────────────────── */
function WorkflowIntelligenceSubview() {
  const navigate = useNavigate();
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-4 gap-4">
        <MetricCard label="P50 latency"   value="42 ms"  tone="success" delta={{ value: '−4 ms',   direction: 'down', baseline: 'vs. last hour', intent: 'positive' }} trend={[55,52,48,46,44,42,42]} />
        <MetricCard label="P95 latency"   value="184 ms" tone="cyan"    delta={{ value: '+12 ms',  direction: 'up',   baseline: 'vs. last hour', intent: 'neutral'  }} trend={[160,165,170,180,184,182,184]} />
        <MetricCard label="P99 latency"   value="412 ms" tone="warning" delta={{ value: '+38 ms',  direction: 'up',   baseline: 'vs. last hour', intent: 'negative' }} trend={[300,320,340,380,410,400,412]} />
        <MetricCard label="Success rate"  value="99.4%"  tone="trust"   delta={{ value: '−0.1pp',  direction: 'down', baseline: 'vs. last hour', intent: 'neutral'  }} trend={[99.7,99.6,99.5,99.6,99.5,99.4,99.4]} />
      </div>
      <Card>
        <Card.Header title="Node hotspots" actions={<Pill tone="warning" dot>2 above SLO</Pill>} />
        <Card.Body>
          <Table
            ariaLabel="Workflow node hotspots"
            density="compact"
            columns={[
              { key: 'workflow', header: 'Workflow', render: (r) => <strong className="text-v2-ink">{r.workflow}</strong> },
              { key: 'node',     header: 'Node',     render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{r.node}</span> },
              { key: 'p95',      header: 'P95 ms',   render: (r) => <span className={'tabular-nums font-semibold ' + (r.p95 > 250 ? 'text-v2-warning' : 'text-v2-ink')}>{r.p95}</span> },
              { key: 'errors',   header: 'Errors / 24h', render: (r) => <span className="tabular-nums">{r.errors}</span> },
              { key: 'verdict',  header: 'SLO',      render: (r) => <Pill tone={r.verdict === 'within' ? 'success' : 'warning'} size="sm" dot>{r.verdict}</Pill> },
            ]}
            rows={[
              { workflow: 'IAM Provisioning',       node: 'okta.assignGroup',     p95: 312, errors: 4, verdict: 'breach' },
              { workflow: 'Threat Triage',          node: 'crowdstrike.scan',     p95: 268, errors: 2, verdict: 'breach' },
              { workflow: 'AppSec Scan',            node: 'snyk.test',            p95: 188, errors: 0, verdict: 'within' },
              { workflow: 'Compliance Evidence',    node: 's3.put',               p95: 142, errors: 1, verdict: 'within' },
              { workflow: 'IR Orchestration',       node: 'servicenow.openCase',  p95:  98, errors: 0, verdict: 'within' },
            ]}
            rowKey={(r, i) => `${r.workflow}-${i}`}
          />
        </Card.Body>
      </Card>
      <Card>
        <Card.Header title="Drift & protection signals" />
        <Card.Body>
          <ul className="space-y-2 text-[12.5px] text-v2-ink-secondary leading-relaxed">
            <li>· Skill <strong className="text-v2-ink">vela.appsec.scanCritical</strong> drifted: false-positive rate <span className="font-semibold text-v2-warning">+18%</span> over 7d.</li>
            <li>· Workflow <strong className="text-v2-ink">IR Orchestration</strong> P99 trending up — investigate <code className="font-mono-v2 text-[11px]">slack.postMessage</code> back-pressure.</li>
            <li>· Protection decisions: <strong className="text-v2-ink">98.6%</strong> of executions cleared all 5 layers; remaining 1.4% mostly <span className="font-semibold">L2 DPG redact</span>.</li>
          </ul>
          <Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/workflow-intelligence')} className="mt-3">Open full Workflow Intelligence</Button>
        </Card.Body>
      </Card>
    </div>
  );
}

/* G118 — proper v2 dashboard pattern. */
import ResolvedGovernanceHubPageV2 from './GovernanceHubPageV2';
export default function ResolvedGovernanceCenterPage(props) {
  return <ResolvedGovernanceHubPageV2 {...props} />;
}

export { GovernanceCenterPageV2Inner };
